import {
  differenceInDays,
  differenceInHours,
  differenceInMinutes,
  differenceInSeconds,
  format,
  formatDistanceToNow,
  isValid,
} from 'date-fns';
import type { Locale } from 'date-fns';
import { be, enUS, pl, ru, uk } from 'date-fns/locale';
import type { TFunction } from 'i18next';

import { Language, TrainingStatus } from '../../shared/constants';
import { ITraining } from '../../types/interfaces/training';

/** Maps app language keys (i18n / user profile) to date-fns locales for relative dates, pickers, etc. */
export function getDateFnsLocale(i18nLanguage: string | undefined): Locale {
  const key = i18nLanguage?.split('-')[0]?.toLowerCase() ?? Language.en;
  switch (key) {
    case Language.ru:
      return ru;
    case Language.pl:
      return pl;
    case Language.by:
      return be;
    case Language.ua:
      return uk;
    default:
      return enUS;
  }
}

export function timeAgo(date, unit = 'day', value = 1) {
  const now = new Date();
  let diff;

  switch (unit) {
    case 'day':
      diff = differenceInDays(now, date);
      break;
    case 'hour':
      diff = differenceInHours(now, date);
      break;
    case 'minute':
      diff = differenceInMinutes(now, date);
      break;
    default:
      throw new Error('Unsupported unit');
  }

  if (diff < value)
    return formatDistanceToNow(new Date(date), { addSuffix: true });
  else return format(new Date(date), 'MMM dd, yy');
}

export function getTime(date) {
  return format(new Date(date), 'HH:mm');
}

export function getDate(date) {
  return format(new Date(date), 'dd MMMM yyyy');
}

export function getTimeValueFromOptionValue(value: number, t: TFunction) {
  const viewValue = {
    minute: Math.floor((value / 60) % 60) || 0,
    second: Math.floor(value % 60) || 0,
    hour: Math.floor((value / 3600) % 24) || 0,
  };

  const minView =
    viewValue.hour > 0
      ? viewValue.minute.toString().padStart(2, '0')
      : viewValue.minute;
  const secView =
    viewValue.hour > 0 || viewValue.minute > 0
      ? viewValue.second.toString().padStart(2, '0')
      : viewValue.second;

  let result = '';

  if (viewValue.hour > 0) {
    result += viewValue.hour + t('common.hShort') + ' ';
  }

  if (viewValue.minute > 0 || viewValue.hour > 0) {
    result += minView + t('common.minShort') + ' ';
  }

  if (viewValue.minute > 0 || viewValue.hour > 0 || viewValue.second > 0) {
    result += secView + t('common.secShort') + ' ';
  }

  return result;
}

export interface FormatTimeByConfigOptions {
  showHours?: boolean;
  showMinutes?: boolean;
  showSeconds?: boolean;
}

/** Format time (seconds) for display, showing only parts enabled in config. */
export function formatTimeValueByConfig(
  value: number,
  t: TFunction,
  options: FormatTimeByConfigOptions,
) {
  const viewValue = {
    hour: Math.floor((value / 3600) % 24) || 0,
    minute: Math.floor((value / 60) % 60) || 0,
    second: Math.floor(value % 60) || 0,
  };

  const showHours = options.showHours !== false;
  const showMinutes = options.showMinutes !== false;
  const showSeconds = options.showSeconds !== false;

  const minView =
    viewValue.hour > 0
      ? viewValue.minute.toString().padStart(2, '0')
      : viewValue.minute;
  const secView =
    viewValue.hour > 0 || viewValue.minute > 0
      ? viewValue.second.toString().padStart(2, '0')
      : viewValue.second;

  let result = '';

  if (showHours && viewValue.hour > 0) {
    result += viewValue.hour + t('common.hShort') + ' ';
  }

  if (
    showMinutes &&
    (viewValue.minute > 0 || viewValue.hour > 0 || viewValue.second > 0)
  ) {
    result += minView + t('common.minShort') + ' ';
  }

  if (
    showSeconds &&
    (viewValue.second > 0 || viewValue.minute > 0 || viewValue.hour > 0)
  ) {
    result += secView + t('common.secShort') + ' ';
  }

  if (result.trim()) return result.trim();
  if (showHours) result += '0' + t('common.hShort') + ' ';
  if (showMinutes) result += '0' + t('common.minShort') + ' ';
  if (showSeconds) result += '0' + t('common.secShort');
  return result.trim();
}

function parseTrainingDate(
  value: Date | string | undefined | null,
): Date | null {
  if (value == null) return null;
  const d = value instanceof Date ? value : new Date(value);
  return isValid(d) ? d : null;
}

export function calculateTrainingTime(training: ITraining): number {
  if (!training || !training.startDate) {
    return 0;
  }
  const start = new Date(training.startDate);
  const finish = new Date(training.finishDate || new Date());
  return differenceInSeconds(finish, start);
}

export function getTrainingDate(training: ITraining): string | null {
  if (!training) return null;

  const start = parseTrainingDate(training.startDate);

  if (training.status === TrainingStatus.started) {
    if (!start) return null;
    return format(start, 'dd.MM.yyyy HH:mm');
  }

  if (training.status === TrainingStatus.finished) {
    if (!start) return null;
    const finish = parseTrainingDate(training.finishDate);
    if (!finish) {
      return format(start, 'dd.MM.yyyy HH:mm');
    }

    const formatStartDay = format(start, 'dd.MM.yyyy');
    const formatEndDay = format(finish, 'dd.MM.yyyy');

    if (formatStartDay === formatEndDay) {
      return `${formatStartDay}: ${format(start, 'HH:mm')} - ${format(finish, 'HH:mm')}`;
    }
    return `${format(start, 'dd.MM.yyyy HH:mm')} - ${format(finish, 'dd.MM.yyyy HH:mm')}`;
  }

  return null;
}
